import { formatPropertyCode } from 'src/modules/shared/utils/catalog/property-code.util';
import {
  PropertyOperation,
  PropertyType,
  PropertySubtype,
  type CatalogPropertyPreviewSummary,
} from 'src/api/generated';
import type {
  ConversationCardTranslate,
  ConversationPropertySummaryViewModel,
} from 'src/modules/communications/types/conversation-property-summary.type';
import type { TranslationKey } from 'src/modules/shared/types/i18n/translation-keys.type';
import { formatCurrency } from 'src/modules/shared/utils/formatting/currency.util';

const hasUsablePrice = (
  amount: string | null | undefined,
  currency: string | null | undefined
): amount is string =>
  amount !== null &&
  amount !== undefined &&
  amount.trim().length > 0 &&
  currency !== null &&
  currency !== undefined &&
  currency.trim().length > 0 &&
  Number.isFinite(Number(amount));

const formatPrice = (
  amount: string | null | undefined,
  currency: string | null | undefined
): string => {
  if (!hasUsablePrice(amount, currency) || !currency) {
    return '';
  }

  return formatCurrency(amount, currency, 0);
};

const propertyTypeTranslationKeys: Record<PropertyType, TranslationKey> = {
  [PropertyType.HOUSE]: 'catalog.properties.propertyType.HOUSE',
  [PropertyType.APARTMENT]: 'catalog.properties.propertyType.APARTMENT',
  [PropertyType.OFFICE]: 'catalog.properties.propertyType.OFFICE',
  [PropertyType.RETAIL]: 'catalog.properties.propertyType.RETAIL',
  [PropertyType.WAREHOUSE]: 'catalog.properties.propertyType.WAREHOUSE',
  [PropertyType.LAND]: 'catalog.properties.propertyType.LAND',
  [PropertyType.PARCEL]: 'catalog.properties.propertyType.PARCEL',
  [PropertyType.PARKING]: 'catalog.properties.propertyType.PARKING',
  [PropertyType.INDUSTRIAL]: 'catalog.properties.propertyType.INDUSTRIAL',
  [PropertyType.OTHER]: 'catalog.properties.propertyType.OTHER',
};

/**
 * Builds the compact localized property title and prices shown on a card.
 *
 * Prices are rendered only for the canonical operation. Sale and rent amounts
 * keep their own currency values, and no operation is inferred from a price.
 *
 * @param property - Authorized summary returned for the linked property.
 * @param propertyId - Internal Comos property ID used for fallback text.
 * @param t - Translator for the active language and real-estate terminology.
 * @returns Compact title, image description, code, operation, and price rows.
 *
 * @example
 * ```ts
 * buildConversationPropertySummaryViewModel(summary, 42, t);
 * ```
 */
export const buildConversationPropertySummaryViewModel = (
  property: CatalogPropertyPreviewSummary,
  propertyId: number,
  t: ConversationCardTranslate
): ConversationPropertySummaryViewModel => {
  const propertyType = property.propertyType
    ? t(
        property.propertyType === PropertyType.APARTMENT &&
          property.propertySubtype === PropertySubtype.STUDIO
          ? 'catalog.properties.propertyType.APARTMENT_STUDIO'
          : propertyTypeTranslationKeys[property.propertyType]
      )
    : t('communications.conversationCard.property.fallbackType');
  const locality = property.locality?.trim();
  const displayTitle = locality
    ? `${propertyType} - ${locality}`
    : propertyType;
  const name = property.name.trim() ? property.name : displayTitle;
  const codeValue = formatPropertyCode({
    displayCode: property.displayCode,
    fallback: `#${propertyId}`,
  });
  const code = t('communications.conversationCard.property.code', {
    id: codeValue,
  });

  const salePrice = formatPrice(
    property.salePriceAmount,
    property.salePriceCurrency
  );
  const rentPrice = formatPrice(
    property.rentPriceAmount,
    property.rentPriceCurrency
  );

  switch (property.operation) {
    case PropertyOperation.SALE:
      return {
        displayTitle,
        name,
        code,
        codeValue,
        operation: t('catalog.properties.operation.SALE'),
        priceRows: [
          {
            label: t('catalog.properties.price.saleLabel'),
            accessibleLabel: t('catalog.properties.price.saleLabel'),
            value: salePrice,
          },
        ],
      };
    case PropertyOperation.RENT:
      return {
        displayTitle,
        name,
        code,
        codeValue,
        operation: t('catalog.properties.operation.RENT'),
        priceRows: [
          {
            label: t('catalog.properties.price.rentLabel'),
            accessibleLabel: t('catalog.properties.price.rentLabel'),
            value: rentPrice,
          },
        ],
      };
    case PropertyOperation.TEMPORARY_RENT:
      return {
        displayTitle,
        name,
        code,
        codeValue,
        operation: t('catalog.properties.operation.TEMPORARY_RENT'),
        priceRows: [
          {
            label: t(
              'communications.conversationCard.property.temporaryRentShort'
            ),
            accessibleLabel: t('catalog.properties.operation.TEMPORARY_RENT'),
            value: rentPrice,
          },
        ],
      };
    case PropertyOperation.SALE_AND_RENT:
      return {
        displayTitle,
        name,
        code,
        codeValue,
        operation: t('catalog.properties.operation.SALE_AND_RENT'),
        priceRows: [
          {
            label: t('catalog.properties.price.saleLabel'),
            accessibleLabel: t('catalog.properties.price.saleLabel'),
            value: salePrice,
          },
          {
            label: t('catalog.properties.price.rentLabel'),
            accessibleLabel: t('catalog.properties.price.rentLabel'),
            value: rentPrice,
          },
        ],
      };
    case null:
    case undefined:
      return {
        displayTitle,
        name,
        code,
        codeValue,
        operation: t(
          'communications.conversationCard.property.operationUnavailable'
        ),
        priceRows: [],
      };
  }
};
